import React, { useState, useMemo } from 'react';
import {
  FiFilter as Filter, FiChevronDown as ChevronDown, FiChevronRight as ChevronRight,
  FiX as X, FiExternalLink as ExternalLink, FiGitBranch as GitCompare,
  FiBookOpen as BookOpen, FiAlertTriangle, FiClock, FiToggleLeft, FiToggleRight
} from 'react-icons/fi';
import { getUtilizationLevel, getUtilizationColorClasses } from '../../../../../hooks/useMemoryAlerts';

interface MemorySnapshot {
  id?: string;
  run_id?: string;
  token_count?: number;
  max_tokens?: number;
  memory_type?: string;
  operation?: string;
  timestamp?: string;
}

interface MemoryFilters {
  memoryType: string;
  operation: string;
  runId: string;
  [key: string]: string;
}

interface RunData {
  runId: string;
  snapshots: MemorySnapshot[];
  startTime: string | undefined;
  endTime: string | undefined;
  peakTokens: number;
  peakUtilization: number;
  pruneCount: number;
  endTokens: number;
  snapshotCount: number;
}

interface Props {
  snapshots: MemorySnapshot[] | null | undefined;
  stats: Record<string, unknown> | null | undefined;
  loading: boolean;
  filters: MemoryFilters;
  updateFilters: (updates: Partial<MemoryFilters>) => void;
  clearFilters: () => void;
  onNavigateToRun: (runId: string) => void;
}

// Color constants
const OPERATION_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  read: { bg: 'bg-green-500', text: 'text-green-600 dark:text-green-400', label: 'Read' },
  write: { bg: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400', label: 'Write' },
  update: { bg: 'bg-yellow-500', text: 'text-yellow-600 dark:text-yellow-400', label: 'Update' },
  prune: { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400', label: 'Prune' },
  other: { bg: 'bg-gray-500', text: 'text-gray-600 dark:text-gray-400', label: 'Other' },
};

interface SkeletonProps {
  className?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded ${className}`} />
);

const formatNumber = (num: number | null | undefined): string => {
  if (!num && num !== 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatTimestamp = (ts: string | undefined): string => {
  if (!ts) return 'N/A';
  return new Date(ts).toLocaleString();
};

const formatDuration = (startTs: string | undefined, endTs: string | undefined): string => {
  if (!startTs || !endTs) return 'N/A';
  const ms = new Date(endTs).getTime() - new Date(startTs).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
};

interface LifecycleStepProps {
  snapshot: MemorySnapshot;
  isLast: boolean;
}

const LifecycleStep: React.FC<LifecycleStepProps> = ({ snapshot, isLast }) => {
  const op = snapshot.operation?.toLowerCase() ?? 'other';
  const colors = OPERATION_COLORS[op] ?? OPERATION_COLORS.other;
  const utilization = snapshot.max_tokens
    ? ((snapshot.token_count! / snapshot.max_tokens) * 100).toFixed(0)
    : '?';

  return (
    <div className="flex items-center">
      <div className="flex flex-col items-center min-w-[80px]">
        <div className={`px-2 py-1 rounded text-xs font-medium text-white ${colors.bg}`}>
          {colors.label}: {formatNumber(snapshot.token_count)}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{utilization}%</span>
      </div>
      {!isLast && (
        <div className="w-6 h-0.5 bg-gray-300 dark:bg-gray-600 mx-1 flex-shrink-0" />
      )}
    </div>
  );
};

const generateRunNarrative = (runData: RunData): string => {
  const { snapshots, peakUtilization, peakTokens, endTokens, pruneCount } = runData;

  const startTokens = snapshots[0]?.token_count ?? 0;
  const parts: string[] = [];

  parts.push(`Started with ${formatNumber(startTokens)} tokens`);
  parts.push(`peaked at ${formatNumber(peakTokens)} (${peakUtilization.toFixed(0)}% utilization)`);

  if (pruneCount > 0) {
    parts.push(`pruned ${pruneCount} time${pruneCount === 1 ? '' : 's'}`);
  }

  parts.push(`ended with ${formatNumber(endTokens)} tokens.`);

  return parts.join(', ') + (pruneCount === 0 ? '.' : '');
};

interface RunStoryCardProps {
  runData: RunData;
  isExpanded: boolean;
  onToggle: (runId: string) => void;
  onNavigateToRun: (runId: string) => void;
  isSelected: boolean;
  onSelect: (runId: string) => void;
  comparisonMode: boolean;
}

const RunStoryCard: React.FC<RunStoryCardProps> = ({ runData, isExpanded, onToggle, onNavigateToRun, isSelected, onSelect, comparisonMode }) => {
  const { level } = getUtilizationLevel(runData.peakUtilization, 100);
  const colors = getUtilizationColorClasses(level);

  let summary: string;
  if (runData.peakUtilization >= 90) {
    summary = 'This run pushed close to its context limit';
  } else if (runData.peakUtilization >= 70) {
    summary = 'This run had moderate context usage';
  } else {
    summary = 'Normal operation';
  }

  return (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden transition-colors ${
      isSelected ? 'ring-2 ring-blue-500' : ''
    }`}>
      {/* Collapsed header */}
      <div
        className="flex items-center justify-between px-5 py-4 bg-white dark:bg-gray-800 cursor-pointer
                   hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        onClick={() => onToggle(runData.runId)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(runData.runId); }} role="button" tabIndex={0}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {comparisonMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelect(runData.runId);
              }}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          )}

          {isExpanded
            ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
            : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
          }

          {/* Run ID link */}
          {runData.runId && runData.runId !== 'unknown' ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNavigateToRun(runData.runId);
              }}
              className="text-sm font-mono text-blue-600 dark:text-blue-400 hover:underline truncate"
            >
              {runData.runId.slice(0, 12)}...
            </button>
          ) : (
            <span className="text-sm font-mono text-gray-400 dark:text-gray-500 truncate">
              No Run ID
            </span>
          )}

          {/* Time range */}
          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 flex-shrink-0">
            <FiClock className="w-3 h-3" />
            {formatDuration(runData.startTime, runData.endTime)}
          </span>

          {/* Snapshot count */}
          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
            {runData.snapshots.length} snapshot{runData.snapshots.length === 1 ? '' : 's'}
          </span>
        </div>

        <div className="flex items-center gap-4 ml-4">
          {/* Peak utilization badge */}
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className={`h-full ${colors.bg}`}
                style={{ width: `${Math.min(runData.peakUtilization, 100)}%` }} />
            </div>
            <span className={`text-sm font-medium ${colors.text} min-w-[40px] text-right`}>
              {runData.peakUtilization.toFixed(0)}%
            </span>
          </div>

          {/* Summary line */}
          <span className="text-xs text-gray-500 dark:text-gray-400 hidden lg:inline max-w-[200px] truncate">
            {summary}
          </span>
        </div>
      </div>

      {/* Expanded body */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 px-5 py-4 space-y-4">
          {/* Lifecycle visualization */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">Memory Lifecycle</p>
            <div className="flex items-center overflow-x-auto pb-2">
              {runData.snapshots.map((s, i) => (
                <LifecycleStep
                  key={s.id ?? `snap-${i}`}
                  snapshot={s}
                  isLast={i === runData.snapshots.length - 1}
                />
              ))}
            </div>
          </div>

          {/* Narrative */}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {generateRunNarrative(runData)}
          </p>

          {/* Quick stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Start</p>
              <p className="text-sm text-gray-900 dark:text-white">{formatTimestamp(runData.startTime)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">End</p>
              <p className="text-sm text-gray-900 dark:text-white">{formatTimestamp(runData.endTime)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Peak Tokens</p>
              <p className="text-sm text-gray-900 dark:text-white">{formatNumber(runData.peakTokens)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Prune Events</p>
              <p className={`text-sm font-medium ${runData.pruneCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                {runData.pruneCount}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Memory Types</p>
              <p className="text-sm text-gray-900 dark:text-white">
                {Array.from(new Set(runData.snapshots.map(s => s.memory_type).filter(Boolean))).join(', ') || 'N/A'}
              </p>
            </div>
          </div>

          {runData.runId && runData.runId !== 'unknown' && (
            <button
              onClick={() => onNavigateToRun(runData.runId)}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              View Full Run Details
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const MemoryTimelineTab: React.FC<Props> = ({
  snapshots,
  loading,
  filters,
  updateFilters,
  clearFilters,
  onNavigateToRun,
}) => {
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState<string[]>([]);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);

  const runGroups = useMemo((): RunData[] => {
    if (!snapshots || snapshots.length === 0) return [];

    const groups: Record<string, MemorySnapshot[]> = {};
    for (const s of snapshots) {
      const runId = s.run_id ?? 'unknown';
      if (!groups[runId]) {
        groups[runId] = [];
      }
      groups[runId].push(s);
    }

    return Object.entries(groups).map(([runId, snaps]) => {
      const sorted = [...snaps].sort((a, b) =>
        new Date(a.timestamp!).getTime() - new Date(b.timestamp!).getTime()
      );

      let peakTokens = 0;
      let peakUtilization = 0;
      let pruneCount = 0;

      for (const s of sorted) {
        if ((s.token_count ?? 0) > peakTokens) peakTokens = s.token_count ?? 0;
        if (s.max_tokens && s.token_count) {
          const util = (s.token_count / s.max_tokens) * 100;
          if (util > peakUtilization) peakUtilization = util;
        }
        if (s.operation?.toLowerCase() === 'prune') pruneCount++;
      }

      const endTokens = sorted[sorted.length - 1]?.token_count ?? 0;

      return {
        runId,
        snapshots: sorted,
        startTime: sorted[0]?.timestamp,
        endTime: sorted[sorted.length - 1]?.timestamp,
        peakTokens,
        peakUtilization,
        pruneCount,
        endTokens,
        snapshotCount: sorted.length,
      };
    })
    .sort((a, b) => new Date(b.startTime!).getTime() - new Date(a.startTime!).getTime());
  }, [snapshots]);

  const displayedRuns = useMemo((): RunData[] => {
    if (!showOnlyIssues) return runGroups;
    return runGroups.filter(r => r.peakUtilization >= 70 || r.pruneCount > 0);
  }, [runGroups, showOnlyIssues]);

  const filterOptions = useMemo(() => {
    const memoryTypes = new Set<string>();
    const operations = new Set<string>();
    if (snapshots) {
      for (const s of snapshots) {
        if (s.memory_type) memoryTypes.add(s.memory_type);
        if (s.operation) operations.add(s.operation);
      }
    }
    return {
      memoryTypes: Array.from(memoryTypes),
      operations: Array.from(operations),
    };
  }, [snapshots]);

  const toggleExpand = (runId: string): void => {
    setExpandedRuns(prev => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  const toggleSelection = (runId: string): void => {
    setSelectedForComparison(prev => {
      if (prev.includes(runId)) return prev.filter(id => id !== runId);
      if (prev.length >= 2) return [prev[1], runId];
      return [...prev, runId];
    });
  };

  const hasActiveFilters = filters.memoryType || filters.operation || filters.runId;

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
            <div className="flex items-center gap-4">
              <Skeleton className="w-5 h-5 rounded" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-20" />
              <div className="flex-1" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!snapshots || snapshots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <BookOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No run stories yet
        </h3>
        <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
          Memory events will be grouped by agent run once agents start executing with memory operations.
          Each run becomes a &ldquo;story&rdquo; showing how the agent used its context window.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-3">
          {/* Filter */}
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
                hasActiveFilters
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              } transition-colors`}
            >
              <Filter className="w-4 h-4" />
              <span className="text-sm">{hasActiveFilters ? 'Filters active' : 'Filter'}</span>
            </button>

            {showFilters && (
              <div className="absolute top-full mt-2 left-0 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 min-w-[280px]">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Filters</span>
                  {hasActiveFilters && (
                    <button onClick={() => { clearFilters(); setShowFilters(false); }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                      Clear all
                    </button>
                  )}
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="memory-type" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Memory Type</label>
                    <select id="memory-type" value={filters.memoryType}
                      onChange={(e) => updateFilters({ memoryType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                      <option value="">All Types</option>
                      {filterOptions.memoryTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="operation" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Operation</label>
                    <select id="operation" value={filters.operation}
                      onChange={(e) => updateFilters({ operation: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                      <option value="">All Operations</option>
                      {filterOptions.operations.map(op => (
                        <option key={op} value={op}>{op}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button onClick={() => setShowFilters(false)}
                  className="mt-4 w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                  Apply Filters
                </button>
              </div>
            )}
          </div>

          {/* Compare Runs */}
          <button
            onClick={() => {
              setComparisonMode(!comparisonMode);
              if (comparisonMode) setSelectedForComparison([]);
            }}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
              comparisonMode
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            } transition-colors`}
          >
            <GitCompare className="w-4 h-4" />
            <span className="text-sm">Compare Runs</span>
          </button>

          {comparisonMode && selectedForComparison.length > 0 && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedForComparison.length}/2 selected
            </span>
          )}

          {/* Show only issues toggle */}
          <button
            onClick={() => setShowOnlyIssues(!showOnlyIssues)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
              showOnlyIssues
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            } transition-colors`}
          >
            {showOnlyIssues
              ? <FiToggleRight className="w-4 h-4" />
              : <FiToggleLeft className="w-4 h-4" />
            }
            <span className="text-sm">Issues only</span>
          </button>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          {displayedRuns.length} run{displayedRuns.length === 1 ? '' : 's'}
          {showOnlyIssues && runGroups.length !== displayedRuns.length && (
            <span className="ml-1">
              (of {runGroups.length} total)
            </span>
          )}
        </div>
      </div>

      {/* Active Filters Pills */}
      {hasActiveFilters && (
        <div className="flex items-center flex-wrap gap-2">
          {filters.memoryType && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
              Type: {filters.memoryType}
              <button onClick={() => updateFilters({ memoryType: '' })} className="ml-2 hover:text-blue-600">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.operation && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
              Operation: {filters.operation}
              <button onClick={() => updateFilters({ operation: '' })} className="ml-2 hover:text-green-600">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.runId && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
              Run: {filters.runId.slice(0, 8)}...
              <button onClick={() => updateFilters({ runId: '' })} className="ml-2 hover:text-purple-600">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Run Story Cards */}
      <div className="space-y-3">
        {displayedRuns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FiAlertTriangle className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              {showOnlyIssues
                ? 'No runs with issues found in this period. Great news!'
                : 'No runs match the current filters.'
              }
            </p>
          </div>
        ) : (
          displayedRuns.map(runData => (
            <RunStoryCard
              key={runData.runId}
              runData={runData}
              isExpanded={expandedRuns.has(runData.runId)}
              onToggle={toggleExpand}
              onNavigateToRun={onNavigateToRun}
              isSelected={selectedForComparison.includes(runData.runId)}
              onSelect={toggleSelection}
              comparisonMode={comparisonMode}
            />
          ))
        )}
      </div>

      {/* Comparison floating bar */}
      {comparisonMode && selectedForComparison.length === 2 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                2 runs selected
              </span>
              <button
                onClick={() => setShowComparisonModal(true)}
                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
              >
                View Comparison
              </button>
              <button
                onClick={() => setSelectedForComparison([])}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comparison Modal (lazy import) */}
      {showComparisonModal && selectedForComparison.length === 2 && (
        <MemoryComparisonModalLazy
          runA={runGroups.find(r => r.runId === selectedForComparison[0])}
          runB={runGroups.find(r => r.runId === selectedForComparison[1])}
          onClose={() => setShowComparisonModal(false)}
        />
      )}
    </div>
  );
};

// Lazy-loaded comparison modal (inline to avoid circular deps)
const MemoryComparisonModalLazy = React.lazy(() => import('./MemoryComparisonModal'));

// Wrap the timeline tab export to provide Suspense for the lazy modal
const MemoryTimelineTabWrapper: React.FC<Props> = (props) => (
  <React.Suspense fallback={null}>
    <MemoryTimelineTab {...props} />
  </React.Suspense>
);

export default MemoryTimelineTabWrapper;
